import React from 'react'
import moment from 'moment-timezone'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import './DetailWalletCardReport.scss'
import LineChartCard from '../../../Components/LineChartCard'
import {useI18n} from '../../../Utils/_helpers/useI18n'
import {MetricEntry, Metrics} from '../../../../services/cellcast.v2.types'
import {useWalletCardReport} from './hooks/useWalletCardReport'
import {ChartRenderer} from './components/ChartRenderer'
import {
  getBaseChartOptions,
  getTotalScanChartOptions,
  getStackedBarChartOptions,
  getCardComparisonOptions,
} from './chartConfigs'
import ReactApexChart from 'react-apexcharts'
import {CHART_COLOR_PALETTES, getLeaderboardColors} from './colors'

export const DetailWalletCardReport = () => {
  const {t} = useI18n()

  // Use custom hook for all state management and API calls
  const {
    whereWhenDate,
    selectedCard,
    cardList,
    comparisonSeries,
    comparisonCategories,
    stackedSeries,
    stackedCategories,
    stackedTotal,
    setWhereWhenDate,
    setSelectedCard,
    reportState,
    whereWhen,
  } = useWalletCardReport()

  const metrics = reportState.data?.data?.metrics as Metrics | undefined
  const dateRangeLabel = reportState.data?.data?.dateRange
    ? `${moment(reportState.data.data.dateRange.startDate).format('MMM D')} - ${moment(
        reportState.data.data.dateRange.endDate
      ).format('MMM D YY')}`
    : ''

  // Helper to map chart data in the format expected by ApexCharts
  const mapChartData = (item: MetricEntry[] = []) => {
    const sum = item?.reduce((acc: number, d: MetricEntry) => acc + (d.value || 0), 0)
    return sum > 0 ? item.map((d: MetricEntry) => [+moment.utc(d.date).format('x'), d.value]) : []
  }

  // Prepare chart data from API
  const newLeadsData = metrics
    ? [
        {
          name: t('WALLET_CARD_REPORT.NEW_LEADS'),
          data: mapChartData(metrics.newLeads.data),
        },
      ]
    : []

  const fromWalletData = metrics
    ? [
        {
          name: t('WALLET_CARD_REPORT.FROM_WALLET'),
          data: mapChartData(metrics.fromWallet.data),
        },
      ]
    : []

  const fromContactData = metrics
    ? [
        {
          name: t('WALLET_CARD_REPORT.FROM_CONTACT'),
          data: mapChartData(metrics.fromContact.data),
        },
      ]
    : []

  const scanWalletCardData = metrics
    ? [
        {
          name: t('WALLET_CARD_REPORT.SCAN_WALLET_CARD'),
          data: mapChartData(metrics.scanWalletCard.data),
        },
      ]
    : []

  const totalScanData = metrics
    ? [
        {
          name: t('WALLET_CARD_REPORT.VIA_WALLET'),
          data: mapChartData(metrics.totalScan.viaWallet),
        },
        {
          name: t('WALLET_CARD_REPORT.VIA_CONTACT'),
          data: mapChartData(metrics.totalScan.viaContact),
        },
      ]
    : []

  const isTotalScanDataEmpty =
    Array.isArray(totalScanData) &&
    totalScanData.length > 0 &&
    totalScanData.every(
      (s) =>
        Array.isArray(s.data) && s.data.every((d: any) => (Array.isArray(d) ? d[1] === 0 : d === 0))
    )

  const chartSeriesToShow = isTotalScanDataEmpty ? [] : totalScanData

  // Get chart options using project color scheme from centralized constants
  const newLeadsOptions = getBaseChartOptions(
    CHART_COLOR_PALETTES.METRICS.NEW_LEADS,
    t('WALLET_CARD_REPORT.NO_DATA_YET')
  )
  const fromWalletOptions = getBaseChartOptions(
    CHART_COLOR_PALETTES.METRICS.FROM_WALLET,
    t('WALLET_CARD_REPORT.NO_DATA_YET')
  )
  const fromContactOptions = getBaseChartOptions(
    CHART_COLOR_PALETTES.METRICS.FROM_CONTACT,
    t('WALLET_CARD_REPORT.NO_DATA_YET')
  )
  const scanWalletCardOptions = getBaseChartOptions(
    CHART_COLOR_PALETTES.METRICS.SCAN_WALLET_CARD,
    t('WALLET_CARD_REPORT.NO_DATA_YET')
  )
  const totalScanChartOptions = getTotalScanChartOptions(t('WALLET_CARD_REPORT.NO_DATA_YET'))
  const stackedBarChartOptions = getStackedBarChartOptions(
    cardList,
    stackedCategories,
    t('WALLET_CARD_REPORT.NO_DATA_YET'),
    t('WALLET_CARD_REPORT.QUANTITY_UNIT')
  )
  const cardComparisonOptions = getCardComparisonOptions(comparisonCategories)

  /* ---------------- Pie Chart based on comparisonSeries --------------- */
  const comparisonPieSeries = comparisonSeries.map((s) =>
    Array.isArray(s.data) ? s.data.reduce((acc, v) => acc + v, 0) : 0
  )

  const comparisonPieOptions = {
    labels: comparisonSeries.map((s) => s.name),
    colors: CHART_COLOR_PALETTES.PROMOTE_ACTIONS,
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '13px',
      markers: {
        width: 12,
        height: 12,
        radius: 4,
      },
      itemMargin: {
        horizontal: 12,
        vertical: 4,
      },
    },
    chart: {
      type: 'pie',
      height: 450,
      fontFamily: 'Poppins, Helvetica, sans-serif',
    },
    plotOptions: {
      pie: {
        donut: {
          size: '0%',
        },
        expandOnClick: false,
      },
    },
    states: {
      hover: {
        filter: {
          type: 'darken',
          value: 0.85,
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: any) {
        return val.toFixed(1) + '%'
      },
      style: {
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: 'Poppins, Helvetica, sans-serif',
        color: '#000',
      },
      dropShadow: {
        enabled: false,
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return val.toFixed(0)
        },
      },
      style: {
        fontSize: '12px',
        fontFamily: 'Poppins, Helvetica, sans-serif',
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
    noData: {text: t('WALLET_CARD_REPORT.NO_DATA_YET')},
  }

  // Prepare chart data objects
  const newLeadsChartData = {
    data: {data: {total: metrics?.newLeads?.total || 0}},
    isLoading: reportState.isFetching,
  }

  const fromWalletChartData = {
    data: {data: {total: metrics?.fromWallet?.total || 0}},
    isLoading: reportState.isFetching,
  }

  const fromContactChartData = {
    data: {data: {total: metrics?.fromContact?.total || 0}},
    isLoading: reportState.isFetching,
  }

  const scanWalletCardChartData = {
    data: {data: {total: metrics?.scanWalletCard?.total || 0}},
    isLoading: reportState.isFetching,
  }

  /* ---------------- Stats Summary Cards ---------------- */
  const totalLeadsCount = stackedTotal?.totalLeads || 0
  const totalLeadsGrowth = stackedTotal?.totalLeadGrowth || 0
  const warmUpCount = stackedTotal?.totalWarmUp || 0
  const warmUpGrowth = stackedTotal?.totalWarmUpGrowth || 0
  const bitesCount = stackedTotal?.totalBites || 0
  const bitesGrowth = stackedTotal?.totalBitesGrowth || 0
  const salesCount = stackedTotal?.totalSales || 0
  const salesGrowth = stackedTotal?.totalSalesGrowth || 0

  // Create chart series data objects
  const newLeadsSeriesData = {
    series: newLeadsData,
    options: newLeadsOptions,
  }

  const fromWalletSeriesData = {
    series: fromWalletData,
    options: fromWalletOptions,
  }

  const fromContactSeriesData = {
    series: fromContactData,
    options: fromContactOptions,
  }

  const scanWalletCardSeriesData = {
    series: scanWalletCardData,
    options: scanWalletCardOptions,
  }

  // -------------------------------- Leaderboard table data ------------------------------
  // Calculate total leads for each series in the stacked bar chart and sort them desc
  const leaderboardData: {name: string; total: number}[] = stackedSeries
    .map((s) => ({name: s.name, total: s.data.reduce((sum, v) => sum + v, 0)}))
    .sort((a, b) => b.total - a.total)
    .slice(0, 10)

  return (
    <div className='detail-wallet-report-container'>
      <div className='mb-5 border-0'>
        <div className='ReportDatePicker'>
          <div className='date-picker-row' role='group' aria-label='Date range selection'>
            <span className='col-5 p-0'>
              <DatePicker
                dateFormat='dd-MM-yyyy'
                className='bg-white rounded-0 border-0 form-control form-control-lg form-control-solid date-picker-start'
                selected={whereWhenDate.start}
                onChange={(date: Date) => setWhereWhenDate({...whereWhenDate, start: date})}
                selectsEnd
                startDate={whereWhenDate.start}
                endDate={whereWhenDate.end}
                maxDate={whereWhenDate.end}
                aria-label='Start date'
                title='Select start date'
              />
            </span>
            <span className='col-5 ps-0'>
              <DatePicker
                dateFormat='dd-MM-yyyy'
                className='bg-white rounded-0 border-0 form-control form-control-lg form-control-solid date-picker-end'
                selected={whereWhenDate.end}
                onChange={(date: Date) => setWhereWhenDate({...whereWhenDate, end: date})}
                selectsEnd
                startDate={whereWhenDate.start}
                endDate={whereWhenDate.end}
                minDate={whereWhenDate.start}
                aria-label='End date'
                title='Select end date'
              />
            </span>
            <i
              className='fas fa-calendar d-flex align-items-center justify-content-center date-picker-icon'
              aria-hidden='true'
            ></i>
          </div>
        </div>

        {/* ---------------- Wallet Card Comparison Chart --------------- */}
        <div className='row g-5 mt-7 wallet-card-comparison-chart'>
          {/* Stacked Bar Chart */}
          <div className='card'>
            <div className='card-header border-0 pt-6'>
              <h3 className='card-title align-items-start flex-column justify-content-start'>
                <span className='card-label fw-bolder fs-4 mb-1'>
                  {t('WALLET_CARD_REPORT.COMPARISON_TITLE')}
                </span>
                <span className='date-total-scan'>{dateRangeLabel}</span>
              </h3>
              <div
                className='row g-3 mt-3 stats-cards-row px-3'
                role='group'
                aria-label='Statistics summary'
              >
                <div className='col'>
                  <div
                    className='matrix-card'
                    role='button'
                    tabIndex={0}
                    aria-label={`Total Leads: ${totalLeadsCount} with ${totalLeadsGrowth}% growth`}
                  >
                    <div className='d-flex flex-column w-100'>
                      <div className='d-flex justify-content-between align-items-center mb-2'>
                        <span className='text-muted fw-bold fs-6'>Total Leads</span>
                      </div>
                      <span className='fw-bolder fs-1 mb-1'>{totalLeadsCount}</span>
                      <span className='text-success fw-bold fs-7'>{totalLeadsGrowth}%</span>
                    </div>
                  </div>
                </div>
                <div className='col'>
                  <div
                    className='matrix-card'
                    role='button'
                    tabIndex={0}
                    aria-label={`Warm up: ${warmUpCount} with ${warmUpGrowth}% growth`}
                  >
                    <div className='d-flex flex-column w-100'>
                      <div className='d-flex justify-content-between align-items-center mb-2'>
                        <span className='text-muted fw-bold fs-6'> Warm up </span>
                      </div>
                      <span className='fw-bolder fs-1 mb-1'>{warmUpCount}</span>
                      <span className='text-success fw-bold fs-7'>{warmUpGrowth}%</span>
                    </div>
                  </div>
                </div>
                <div className='col'>
                  <div
                    className='matrix-card'
                    role='button'
                    tabIndex={0}
                    aria-label={`Bites: ${bitesCount} with ${bitesGrowth}% growth`}
                  >
                    <div className='d-flex flex-column w-100'>
                      <div className='d-flex justify-content-between align-items-center mb-2'>
                        <span className='text-muted fw-bold fs-6'>Bites</span>
                      </div>
                      <span className='fw-bolder fs-1 mb-1'>{bitesCount}</span>
                      <span className='text-success fw-bold fs-7'>{bitesGrowth}%</span>
                    </div>
                  </div>
                </div>
                <div className='col'>
                  <div
                    className='matrix-card'
                    role='button'
                    tabIndex={0}
                    aria-label={`Sales: ${salesCount} with ${salesGrowth}% growth`}
                  >
                    <div className='d-flex flex-column w-100'>
                      <div className='d-flex justify-content-between align-items-center mb-2'>
                        <span className='text-muted fw-bold fs-6'>Sales</span>
                      </div>
                      <span className='fw-bolder fs-1 mb-1'>{salesCount}</span>
                      <span className='text-success fw-bold fs-7'>{salesGrowth}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='card-body pt-2'>
              <ChartRenderer
                options={stackedBarChartOptions}
                series={stackedSeries}
                type='bar'
                height={365}
                noDataText={t('WALLET_CARD_REPORT.NO_DATA_YET')}
              />
            </div>
          </div>
        </div>

        {/* Card Product Comparison Chart with Dropdown & Pie */}
        <div className='row g-5 mt-7 product-comparison-chart'>
          {/* Card with both charts */}
          <div className='col-12 p-0'>
            <div className='card'>
              <div className='card-header border-0 pt-6 d-flex flex-row align-items-center justify-content-between'>
                <h3 className='card-title align-items-start flex-column'>
                  <span className='card-label fw-bolder fs-4 mb-1'>Promote Actions</span>
                  <span className='date-total-scan'>{dateRangeLabel}</span>
                </h3>
                <div className='card-filter'>
                  <label className='form-label me-2 fw-bold' htmlFor='card-select'>
                    Select card:
                  </label>
                  <select
                    id='card-select'
                    className='form-select form-select-solid select-card'
                    style={{width: '250px'}}
                    value={selectedCard}
                    onChange={(e) => setSelectedCard(e.target.value)}
                    aria-label='Select card for comparison'
                    title='Choose a card to view its promote actions'
                  >
                    {cardList.map((card) => (
                      <option key={card} value={card}>
                        {card}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='card-body pt-2'>
                <div className='chart-promote-action-container'>
                  {/* Bar Chart */}
                  <div className='col-lg-8 col-md-12 bar-chart-container'>
                    <ChartRenderer
                      options={cardComparisonOptions}
                      series={comparisonSeries}
                      type='bar'
                      height={380}
                      noDataText={t('WALLET_CARD_REPORT.NO_DATA_YET')}
                    />
                  </div>
                  {/* Pie Chart derived from same data */}
                  <div
                    className='col-lg-4 col-md-12 col-sm-12 d-flex align-items-center justify-content-center pie-chart-container'
                    style={{paddingTop: '60px'}}
                  >
                    <ChartRenderer
                      options={comparisonPieOptions as any}
                      series={comparisonPieSeries as any}
                      type='pie'
                      height={450}
                      noDataText={t('WALLET_CARD_REPORT.NO_DATA_YET')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Leader Board Chart */}
        <div className='row g-5 mt-7 leader-board-chart px-6'>
          <div className='card'>
            <div className='card-header border-0 pt-6'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-4 mb-1'>Leader Board</span>
              </h3>
            </div>
            <div
              className='card-body pt-2'
              role='region'
              aria-label='Leaderboard chart showing top performing cards'
            >
              {(() => {
                // Use leaderBoard data; when empty, we'll hide axes
                const hasData = leaderboardData.length > 0
                const leaderData = hasData ? leaderboardData : []
                // Prepare data for horizontal bar chart
                const categories = leaderData.map((item) => item.name)
                const seriesData = leaderData.map((item) => item.total)

                // Define colors using project theme from centralized constants
                const colors = getLeaderboardColors(leaderData.length)

                const options = {
                  chart: {
                    type: 'bar',
                    height: 450,
                    toolbar: {show: false},
                    fontFamily: 'Poppins, Helvetica, sans-serif',
                  },
                  plotOptions: {
                    bar: {
                      horizontal: true,
                      distributed: true,
                      barHeight: '75%',
                      borderRadius: 0,
                      dataLabels: {
                        position: 'top',
                      },
                    },
                  },
                  colors: colors,
                  dataLabels: {
                    enabled: true,
                    textAnchor: 'middle',
                    distributed: true,
                    formatter: function (val: any, opt: any) {
                      const index = opt.dataPointIndex
                      if (index === 0) return '1st'
                      else if (index === 1) return '2nd'
                      else if (index === 2) return '3rd'
                      return ''
                    },
                    offsetX: -250,
                    style: {
                      fontSize: '14px',
                      fontWeight: 700,
                      colors: ['#ffffff'],
                    },
                    background: {
                      enabled: false,
                    },
                    dropShadow: {
                      enabled: false,
                    },
                  },
                  xaxis: {
                    categories: categories,
                    labels: {
                      show: true,
                      style: {
                        colors: '#6B7280',
                        fontSize: '12px',
                      },
                    },
                    axisBorder: {
                      show: hasData,
                      color: '#E5E7EB',
                      offsetY: 0,
                    },
                    axisTicks: {
                      show: false,
                    },
                  },
                  yaxis: {
                    labels: {
                      show: hasData,
                      style: {
                        colors: '#181C32',
                        fontSize: '14px',
                        fontWeight: 600,
                      },
                    },
                  },
                  grid: {
                    borderColor: '#E5E7EB',
                    strokeDashArray: 3,
                    xaxis: {
                      lines: {
                        show: true,
                      },
                    },
                    yaxis: {
                      lines: {
                        show: false,
                      },
                    },
                    padding: {
                      left: 10,
                      right: 30,
                    },
                  },
                  tooltip: {
                    enabled: true,
                    y: {
                      formatter: function (val: number) {
                        return val + ' leads'
                      },
                    },
                  },
                  legend: {
                    show: false,
                  },
                } as any

                const series = [
                  {
                    name: 'Total Leads',
                    data: seriesData,
                  },
                ]

                return (
                  <ChartRenderer
                    options={options}
                    series={series}
                    type='bar'
                    height={400}
                    noDataText={t('WALLET_CARD_REPORT.NO_DATA_YET')}
                  />
                )
              })()}
            </div>
          </div>
        </div>

        {true ? (
          <div className='text-center my-5'>{/* <span>Loading...</span> */}</div>
        ) : (
          <>
            {/* Chart Row - Styled like ChartDashboard */}
            <div className='d-flex flex-row dashboard_chart_row'>
              <LineChartCard
                whereWhen={whereWhen}
                chartData={newLeadsChartData}
                seriesData={newLeadsSeriesData}
                title={t('WALLET_CARD_REPORT.NEW_LEADS')}
                toolTipText={t('WALLET_CARD_REPORT.NEW_LEADS')}
              />

              <LineChartCard
                whereWhen={whereWhen}
                chartData={fromWalletChartData}
                seriesData={fromWalletSeriesData}
                title={t('WALLET_CARD_REPORT.FROM_WALLET')}
                toolTipText={t('WALLET_CARD_REPORT.FROM_WALLET')}
              />

              <LineChartCard
                whereWhen={whereWhen}
                chartData={fromContactChartData}
                seriesData={fromContactSeriesData}
                title={t('WALLET_CARD_REPORT.FROM_CONTACT')}
                toolTipText={t('WALLET_CARD_REPORT.FROM_CONTACT')}
              />

              <LineChartCard
                whereWhen={whereWhen}
                chartData={scanWalletCardChartData}
                seriesData={scanWalletCardSeriesData}
                title={t('WALLET_CARD_REPORT.SCAN_WALLET_CARD')}
                toolTipText={t('WALLET_CARD_REPORT.SCAN_WALLET_CARD')}
              />
            </div>

            {/* Total Scan Chart - Matched to ChartDashboard style */}
            <div className='d-flex flex-row mt-7 report-total-scan-chart'>
              <div className='col-lg-12 py-6'>
                <div className='card h-450px'>
                  <div className='card-header border-0 pt-5'>
                    <h3 className='card-title align-items-start flex-column'>
                      <span className='card-label fw-bolder fs-4 mb-1'>
                        {t('WALLET_CARD_REPORT.TOTAL_SCAN')}
                      </span>
                      <span className='date-total-scan'>{dateRangeLabel}</span>
                    </h3>
                    <div className='custom-legend d-flex align-items-start flex-column'>
                      <div className='legend-item'>
                        <div className='legend-line legend-wallet'></div>
                        <span className='legend-label'>{t('WALLET_CARD_REPORT.VIA_WALLET')}</span>
                      </div>
                      <div className='legend-item'>
                        <div className='legend-line legend-contact'></div>
                        <span className='legend-label'>{t('WALLET_CARD_REPORT.VIA_CONTACT')}</span>
                      </div>
                    </div>
                  </div>
                  <div className=''>
                    <div className=''>
                      <ReactApexChart
                        options={totalScanChartOptions}
                        series={chartSeriesToShow}
                        type='area'
                        height={365}
                        width='100%'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
